import { UISpace, ACLEntryWithUser, transformBackendSpaceToUI, SpaceEntityBackend } from '../types/spaces';
import { AnyWindowEntity } from '../types/windows';
import fetchService from './fetchService';
import { API_BASE_URL } from '../api/config';
import { createFileLogger, formatJSON } from '../utils/logger';
import { stripPerUserSpaceUpdate } from '../utils/spaceSharedFields';

const log = createFileLogger('SPACESERVICE_SERVICE', { enabled: false });

/**
 * Diagnostic: warn if the spaceId being PUT to does not match the currently
 * active space. This catches the closure-vs-store mismatch class of bugs
 * where stale debounced/setTimeout callbacks fire after a space switch and
 * write the new space's data into the old space's row (or vice versa).
 *
 * Imported lazily to avoid a circular dependency between spaceService and
 * spaceStore.
 */
function logSpaceMismatch(callsite: string, argSpaceId: string): void {
  try {
    // Lazy require to break the spaceStore <-> spaceService cycle.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useSpaceStore } = require('../stores/spaceStore');
    const activeSpaceId = useSpaceStore.getState().activeSpace?.id;
    if (activeSpaceId && argSpaceId !== activeSpaceId) {
      // eslint-disable-next-line no-console
      console.warn('[SpaceService][SpaceMismatch]', {
        callsite,
        argSpaceId,
        activeSpaceId,
        stack: new Error().stack?.split('\n').slice(2, 6).join('\n'),
      });
    }
  } catch {
    // Module not yet loaded — safe to ignore; spaceStore initializes before
    // any meaningful PUT can happen.
  }
}

export class SpaceService {
  private baseUrl = `${API_BASE_URL}/spaces`;
  
  // ✅ PERFORMANCE FIX: Track pending space updates to prevent duplicates
  private pendingSpaceUpdates: Map<string, Promise<UISpace>> = new Map();

  private getAuthHeaders() {
    const authToken = localStorage.getItem('authToken');
    return {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
  }

  async fetchSpace(id: string): Promise<UISpace> {
    log.log('[SpaceService] 🔍 Fetching space from backend:', id);
    
    const backendResponse: any = await fetchService(`${this.baseUrl}/${id}`, {
      credentials: 'include',
    });
    
    log.log('[SpaceService] 📦 RAW BACKEND RESPONSE:', JSON.stringify(backendResponse, null, 2));
    log.log('[SpaceService] 📦 Backend response summary:', {
      _id: backendResponse._id,
      id: backendResponse.id,
      name: backendResponse.name,
      org_id: backendResponse.org_id,
      hasSettings: !!backendResponse.settings,
      hasWindows: !!backendResponse.windows,
      hasSkeleton: !!backendResponse.skeleton,
      skeletonKeys: backendResponse.skeleton ? Object.keys(backendResponse.skeleton) : []
    });
    
    const transformed = transformBackendSpaceToUI(backendResponse);
    
    log.log('[SpaceService] 📦 TRANSFORMED UI SPACE:', JSON.stringify(transformed, null, 2));
    log.log('[SpaceService] 📦 Transformed summary:', {
      id: transformed.id,
      name: transformed.name,
      org_id: transformed.org_id,
      windowCount: Object.keys(transformed.windows || {}).length,
      hasSettings: !!transformed.settings
    });
    
    return transformed;
  }

  async updateSpace(
    id: string,
    windows: Record<string, AnyWindowEntity>,
    settings: any,
    activeWindowId?: string | null,
    contextEntities?: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>,
    contextExcerpts?: any[]
  ): Promise<UISpace> {
    // ✅ PERFORMANCE FIX: Deduplicate simultaneous space updates
    // If there's already a pending update for this space, return that promise instead
    const existingUpdate = this.pendingSpaceUpdates.get(id);
    if (existingUpdate) {
      log.info(`[SpaceService] ⚡ Deduplicating space update for ${id} (update already in progress)`);
      return existingUpdate;
    }
    
    // Create the update promise
    const updatePromise = this.performSpaceUpdate(id, windows, settings, activeWindowId, contextEntities, contextExcerpts);
    
    // Track this pending update
    this.pendingSpaceUpdates.set(id, updatePromise);
    
    // Clean up after completion (success or failure)
    updatePromise
      .finally(() => {
        this.pendingSpaceUpdates.delete(id);
      });
    
    return updatePromise;
  }
  
  // ✅ NEW: Actual space update implementation (separated for deduplication)
  private async performSpaceUpdate(
    id: string,
    windows: Record<string, AnyWindowEntity>,
    settings: any,
    activeWindowId?: string | null,
    contextEntities?: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>,
    contextExcerpts?: any[]
  ): Promise<UISpace> {
    try {
      // Diagnostic: detect closure-vs-store mismatch (stale spaceId from a
      // debounced timer firing after a space switch). See spaceCRDTService
      // historical bug; the strip below makes the windows-leak harmless but
      // a mismatch here still signals a buggy caller worth investigating.
      logSpaceMismatch('spaceService.updateSpace', id);

      log.debug(formatJSON({
        windowCount: Object.keys(windows || {}).length,
        hasContextEntities: !!contextEntities,
        contextEntityCount: Object.keys(contextEntities || {}).length
      }, `[SpaceService] persisting space ${id} to backend (per-user fields will be stripped)`));

      // Strip per-user fields (windows, activeWindowId, per-user settings).
      // Per-user state lives in the per-user `space:{spaceId}:{userId}` Y.Doc,
      // never in the shared `entities.skeleton.*` row.
      const safe = stripPerUserSpaceUpdate(
        { windows, settings, activeWindowId, contextEntities, contextExcerpts },
        'spaceService.updateSpace',
      );

      const updatePayload: Record<string, unknown> = {};
      if (safe.settings !== undefined) updatePayload.settings = safe.settings;

      // contextEntities/contextExcerpts: always send when provided so the
      // backend can clear them (empty {} clears, absent leaves untouched).
      // The strip helper preserves the empty-clears semantics.
      if (safe.contextEntities !== undefined) updatePayload.contextEntities = safe.contextEntities;
      if (safe.contextExcerpts !== undefined) updatePayload.contextExcerpts = safe.contextExcerpts;

      // Nothing left to send (all per-user) — short-circuit so we don't burn a
      // round-trip just to rewrite `updated_at`.
      if (Object.keys(updatePayload).length === 0) {
        log.debug(`[SpaceService] skipping PUT for space ${id} — payload is entirely per-user`);
        // Caller expects a UISpace return; refetch is the safest option but
        // most callers ignore the return value. Avoid the extra GET unless
        // it ends up needed (none of the current callers use the return).
        return undefined as unknown as UISpace;
      }

      const space: SpaceEntityBackend = await fetchService(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updatePayload),
        credentials: 'include',
      });

      log.debug(`[SpaceService] space ${id} persisted (shared fields only)`);

      return transformBackendSpaceToUI(space);
    } catch (error) {
      log.error('[SpaceService] ❌ Space update failed:', {
        error: error instanceof Error ? error.message : String(error),
        spaceId: id,
      });

      // ✅ FIX: Rethrow the error so callers know the save failed
      // Silent failures are worse than visible errors
      throw error;
    }
  }

  // Helper method to sanitize window data for backend validation
  private sanitizeWindowsForBackend(windows: Record<string, AnyWindowEntity>): Record<string, AnyWindowEntity> {
    const sanitizedWindows: Record<string, AnyWindowEntity> = {};
    
    Object.entries(windows).forEach(([windowId, window]) => {
      try {
        // Deep clone and sanitize the window object
        const sanitized = JSON.parse(JSON.stringify(window, (key, value) => {
          // Remove undefined values (but keep null) and functions
          if (value === undefined || typeof value === 'function') {
            // CRITICAL: Return undefined for undefined values so JSON.stringify omits them
            // Don't return null - that changes undefined to null and sends it to backend
            return undefined;
          }
          // Remove circular references
          if (typeof value === 'object' && value !== null) {
            if (value.__proto__ && value.__proto__.constructor !== Object && value.__proto__.constructor !== Array) {
              return undefined;
            }
          }
          return value;
        }));
        
        // Ensure required fields are present
        if (sanitized && sanitized.id && sanitized.type === 'window') {
          sanitizedWindows[windowId] = sanitized;
        } else {
          log.warn('[SpaceService] Skipping invalid window during sanitization:', windowId, sanitized);
        }
      } catch (error) {
        log.error('[SpaceService] Failed to sanitize window:', windowId, error);
      }
    });
    
    // Log only if windows were actually removed during sanitization
    const removedCount = Object.keys(windows).length - Object.keys(sanitizedWindows).length;
    if (removedCount > 0) {
      log.warn('[SpaceService] Windows removed during sanitization:', {
        originalCount: Object.keys(windows).length,
        sanitizedCount: Object.keys(sanitizedWindows).length,
        removedCount
      });
    }
    
    return sanitizedWindows;
  }

  // Helper method to clean up the windows object before saving to backend
  private cleanupWindowsObject(windows: Record<string, AnyWindowEntity>): Record<string, AnyWindowEntity> {
    // Filter out incomplete window entries (ghost windows)
    const cleanedWindows: Record<string, AnyWindowEntity> = {};
    
    Object.entries(windows).forEach(([windowId, window]) => {
      // Log what we received for debugging
      if (!window || !window.id || window.type !== 'window') {
        console.error(`[SpaceService] ⚠️ Incomplete window detected:`, {
          windowId,
          hasWindow: !!window,
          hasId: !!(window?.id),
          type: window?.type,
          hasPosition: !!(window?.position),
          hasSize: !!(window?.size),
          hasAppType: !!(window?.appType),
          keys: window ? Object.keys(window) : []
        });
      }
      
      // Check if this is a valid, complete window
      // A valid window should have at least id and type
      if (window && 
          window.id && 
          window.type === 'window') {
        cleanedWindows[windowId] = window;
      } else {
        log.log(`[SpaceService] Removing incomplete window (missing id or type): ${windowId}`, window);
      }
    });
    
    return cleanedWindows;
  }

  /**
   * Clean ghost / malformed window entries from a space.
   *
   * Windows are now per-user CRDT state in `space:{spaceId}:{userId}`, so the
   * REST `PUT /api/spaces/{id}` payload no longer carries `windows` and a
   * cleanup write would be a no-op. Instead we rebuild the in-memory window
   * map from the store, drop entries that fail validation, and write the
   * cleaned set back through the per-user CRDT.
   *
   * Exposed via `window.spaceTools.cleanupGhostWindows()` for developer
   * console use; not called on hot paths.
   */
  async cleanupSpace(id: string): Promise<UISpace> {
    try {
      // Lazy require to avoid the spaceService <-> spaceStore cycle at module
      // load time. Both are singletons so by the time this runs they're ready.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { useSpaceStore } = require('../stores/spaceStore');
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { spaceCRDTService } = require('./crdt/spaceCRDTService');

      const store = useSpaceStore.getState();
      const localWindows: Record<string, AnyWindowEntity> = store.windows || {};
      const cleanedWindows = this.cleanupWindowsObject(localWindows);

      const removedIds = Object.keys(localWindows).filter(wid => !cleanedWindows[wid]);
      log.log(`[SpaceService.cleanupSpace] removing ${removedIds.length} ghost windows from per-user CRDT for space ${id}`);

      for (const wid of removedIds) {
        try {
          spaceCRDTService.removeWindow(id, wid);
        } catch (err) {
          log.warn(`[SpaceService.cleanupSpace] removeWindow failed for ${wid}:`, err);
        }
      }

      // Refetch so the caller sees the canonical (shared-only) backend view.
      const refreshed = await this.getSpace(id);
      if (!refreshed) throw new Error(`Space not found after cleanup: ${id}`);

      // Local store already reflects per-user removals via spaceCRDTService.
      // Overlay the refreshed shared fields.
      return { ...refreshed, windows: cleanedWindows } as UISpace;
    } catch (error) {
      log.error('[SpaceService] Error cleaning up space:', error);
      throw error;
    }
  }

  private toUISpace(space: SpaceEntityBackend): UISpace {
    return transformBackendSpaceToUI(space);
  }

  async createSpace(name: string, templateType?: string, templateContent?: string, orgId?: string | null, settings?: any, skipProjectLimit?: boolean): Promise<UISpace> {
    log.log('[SPACESERVICE_CREATE] 🛠️ Creating space:', { name, templateType, hasContent: !!templateContent, orgId, hasSettings: !!settings });
    
    // Always let the backend generate the ID
    const payload: any = { name };
    if (templateType) {
      payload.templateType = templateType;
    }
    if (templateContent) {
      payload.templateContent = templateContent;
    }
    if (orgId) {
      payload.org_id = orgId;
    }
    if (settings) {
      payload.settings = settings;
    }
    if (skipProjectLimit) {
      payload.skip_project_limit = true;
    }
    
    log.log('[SPACESERVICE_CREATE] 📤 Sending request to:', this.baseUrl);
    log.log('[SPACESERVICE_CREATE] 📤 Payload:', payload);
    
    const space: SpaceEntityBackend = await fetchService(`${this.baseUrl}`, {
      method: 'POST',
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    
    log.log('[SPACESERVICE_CREATE] ✅ Space created successfully:', { id: space._id, name: space.name });
    
    // Space creation event tracking is intentionally not emitted here.
    
    return transformBackendSpaceToUI(space);
  }

  async getSpace(id: string): Promise<UISpace | null> {
    try {
      return await this.fetchSpace(id);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  // async updateWindow(windowId: string, updates: Partial<AnyWindowEntity>) {
  //   const space = await this.fetchSpace(windowId.split('-')[0]);
  //   const updatedWindows = space.windows.map((w) =>
  //     w.id === windowId ? ({ ...w, ...updates } as AnyWindowEntity) : w,
  //   );
  //   return this.updateSpace(space.id, updatedWindows as AnyWindowEntity[], space.settings);
  // }

  /**
   * Fetch the caller's Desktops (scoped by /api/spaces/list — owner + explicit
   * per-user invite only; never org-wide ACL).
   *
   * @param ownedOnly When true, restricts to Desktops the caller actually owns
   *   (excludes teammates' Desktops shared via invite). Use this for
   *   landing/default-destination flows where a shared Desktop must never be
   *   treated as the caller's own.
   */
  async fetchRecentSpaces(ownedOnly = false): Promise<UISpace[]> {
    const url = ownedOnly ? `${this.baseUrl}/list?owned_only=true` : `${this.baseUrl}/list`;
    const spacesData: SpaceEntityBackend[] = await fetchService(url, {
      credentials: 'include',
    });
    
    // Filter out trashed spaces (soft delete)
    const activeSpacesData = spacesData.filter(space => !space.trashed);
    
    const spaces = activeSpacesData.map(space => transformBackendSpaceToUI(space));
    return spaces;
  }

  async fetchRecentSpace(): Promise<UISpace | null> {
    try {
      const space: SpaceEntityBackend = await fetchService(`${this.baseUrl}/recent`, {
        credentials: 'include',
      });
      
      return transformBackendSpaceToUI(space);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      log.error('[SpaceService] Error fetching recent space:', error);
      throw error;
    }
  }

  async updateSpaceName(spaceId: string, newName: string) {
    log.log('[SpaceService] updateSpaceName called:', { spaceId, newName });
    
    try {
      const url = `${this.baseUrl}/${spaceId}`;
      const payload = { name: newName };
      
      log.log('[SpaceService] Sending PATCH request:', { url, payload });
      
      const response = await fetchService(url, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      
      log.log('[SpaceService] PATCH request successful, response:', response);
      
      return true;
    } catch (error) {
      log.error('[SpaceService] Failed to update space name:', error);
      throw error;
    }
  }

  // NEW ACL METHODS

  async addPermission(spaceId: string, email: string, role: 'owner' | 'editor' | 'viewer' | 'commenter'): Promise<void> {
    try {
      await fetchService(`${this.baseUrl}/${spaceId}/permissions`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
        credentials: 'include',
      });
    } catch (error) {
      log.error('[SpaceService] Error adding permission:', error);
      throw error;
    }
  }

  async removePermission(spaceId: string, userAuth0Id: string): Promise<void> {
    try {
      await fetchService(`${this.baseUrl}/${spaceId}/permissions/${userAuth0Id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch (error) {
      log.error('[SpaceService] Error removing permission:', error);
      throw error;
    }
  }

  async listPermissions(spaceId: string): Promise<{ permissions: ACLEntryWithUser[] }> {
    try {
      const response = await fetchService(`${this.baseUrl}/${spaceId}/permissions`, {
        credentials: 'include',
      });
      return response;
    } catch (error) {
      log.error('[SpaceService] Error listing permissions:', error);
      throw error;
    }
  }
}

export const spaceService = new SpaceService();
